import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", tagline: "", description: "", category: "Black Tea", features: [], pack_sizes: [], moq: "", images: [], order: 10, active: true, future: false };

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setList = (k) => (e) => setForm({ ...form, [k]: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const del = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); load(); };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-forest">Product Management</h1>
        <Button onClick={openNew} data-testid="new-product-btn" className="rounded-full bg-forest hover:bg-olive text-cream transition-colors"><Plus size={16} className="mr-2" /> New Product</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-forest/10 overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="h-32 overflow-hidden bg-cream">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="border-olive/40 text-olive text-xs">{p.category}</Badge>
                {p.future && <Badge className="bg-gold text-ink text-xs">Coming Soon</Badge>}
              </div>
              <h3 className="font-display text-lg text-forest">{p.name}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.description}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="rounded-full border-forest/20 text-forest"><Pencil size={14} className="mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`} className="text-red-600"><Trash2 size={14} /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-editor-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-forest">{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-forest">Name</Label><Input value={form.name} onChange={set("name")} data-testid="product-name" className="border-forest/15" /></div>
              <div className="space-y-1.5"><Label className="text-forest">Category</Label><Input value={form.category} onChange={set("category")} data-testid="product-category" className="border-forest/15" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-forest">Tagline</Label><Input value={form.tagline} onChange={set("tagline")} data-testid="product-tagline" className="border-forest/15" /></div>
            <div className="space-y-1.5"><Label className="text-forest">Description</Label><Textarea rows={3} value={form.description} onChange={set("description")} data-testid="product-description" className="border-forest/15" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-forest">Features (one per line)</Label><Textarea rows={4} value={(form.features || []).join("\n")} onChange={setList("features")} data-testid="product-features" className="border-forest/15" /></div>
              <div className="space-y-1.5"><Label className="text-forest">Pack Sizes (one per line)</Label><Textarea rows={4} value={(form.pack_sizes || []).join("\n")} onChange={setList("pack_sizes")} data-testid="product-packs" className="border-forest/15" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-forest">Image URLs (one per line)</Label><Textarea rows={3} value={(form.images || []).join("\n")} onChange={setList("images")} data-testid="product-images" className="border-forest/15" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-forest">MOQ</Label><Input value={form.moq} onChange={set("moq")} data-testid="product-moq" className="border-forest/15" /></div>
              <div className="space-y-1.5"><Label className="text-forest">Sort Order</Label><Input type="number" value={form.order} onChange={set("order")} data-testid="product-order" className="border-forest/15" /></div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-forest"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="product-active" /> Active</label>
              <label className="flex items-center gap-2 text-sm text-forest"><input type="checkbox" checked={form.future} onChange={(e) => setForm({ ...form, future: e.target.checked })} data-testid="product-future" /> Coming Soon</label>
            </div>
            <Button onClick={save} data-testid="save-product" className="w-full rounded-full bg-forest hover:bg-olive text-cream transition-colors">{editing ? "Update Product" : "Create Product"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
